/**
 * Portal context — determines whether the current hostname is the main platform
 * portal (e.g. localhost / citybus.com.np) or a tenant subdomain portal
 * (e.g. test.localhost / test.citybus.com.np).
 *
 * Ground truth comes from window.location.hostname, not the auth store, so it
 * is always correct even before the user logs in.
 */

import { BASE_DOMAIN } from './baseDomain'

export interface Municipality {
  slug: string
  name_en: string
  name_ne: string
}

export interface PortalContext {
  isTenantPortal: boolean
  tenantSlug: string | null
  isMunicipalityPortal: boolean
  municipality: Municipality | null
}

/** The subdomain when this host is a non-main (tenant or municipality) host, else null. */
function hostSubdomain(): string | null {
  const hostname = window.location.hostname
  const parts = hostname.split('.')
  if (parts.length === 1 || parts[0] === 'www') return null
  if (hostname.endsWith('.localhost')) return parts[0]
  if (BASE_DOMAIN && hostname.endsWith(`.${BASE_DOMAIN}`)) return parts[0]
  return null
}

const cacheKey = () => `kvbms_portal:${window.location.hostname}`

function cachedMunicipality(): Municipality | null {
  try {
    const raw = sessionStorage.getItem(cacheKey())
    const p = raw && JSON.parse(raw)
    if (p && p.kind === 'municipality' && typeof p.slug === 'string') {
      return { slug: p.slug, name_en: p.name_en ?? '', name_ne: p.name_ne ?? '' }
    }
  } catch { /* storage blocked or bad JSON: behave as a plain tenant host */ }
  return null
}

/**
 * Ask the server what kind of host this is. Awaited before the first render; never throws,
 * and a failure leaves the existing tenant behaviour. Main hosts skip the call.
 */
export async function loadPortalKind(): Promise<void> {
  if (!hostSubdomain()) return
  try { if (sessionStorage.getItem(cacheKey())) return } catch { /* ignore */ }
  try {
    // Plain fetch: no auth and no tenant header on this call.
    const res = await fetch('/api/v1/public/portal/', { signal: AbortSignal.timeout(3000) })
    if (!res.ok) return
    const body = await res.json()
    const data = body && typeof body === 'object' && 'data' in body ? body.data : body
    if (data && (data.kind === 'municipality' || data.kind === 'main')) {
      try { sessionStorage.setItem(cacheKey(), JSON.stringify(data)) } catch { /* ignore */ }
    }
  } catch { /* offline, timeout, bad body: fall back to tenant behaviour */ }
}

export function getPortalContext(): PortalContext {
  const slug = hostSubdomain()
  if (!slug) return { isTenantPortal: false, tenantSlug: null, isMunicipalityPortal: false, municipality: null }
  const municipality = cachedMunicipality()
  if (municipality) return { isTenantPortal: false, tenantSlug: null, isMunicipalityPortal: true, municipality }
  return { isTenantPortal: true, tenantSlug: slug, isMunicipalityPortal: false, municipality: null }
}

/** Build the login URL for a specific tenant subdomain. */
export function getTenantLoginUrl(tenantSchema: string): string {
  const { protocol, port } = window.location
  const portSuffix = port ? `:${port}` : ''
  const baseDomain = BASE_DOMAIN || 'localhost'
  return `${protocol}//${tenantSchema.replace(/_/g, '-')}.${baseDomain}${portSuffix}/login`
}

/** Build the login URL for the main (platform) domain. */
export function getMainLoginUrl(): string {
  const { protocol, port } = window.location
  const portSuffix = port ? `:${port}` : ''
  const baseDomain = BASE_DOMAIN || 'localhost'
  return `${protocol}//${baseDomain}${portSuffix}/login`
}
